import {Component, ViewChild} from '@angular/core';
import {KENDO_DROPDOWNS} from '@progress/kendo-angular-dropdowns';

import {BoiteDialogueGeneriqueComponent, BoiteDialogueMessagesComponent, ControleConteneurComponent, SimplePanelComponent} from '../../../../../../val-ammon-composants/val-composants-lib/src/public-api';
import {AeFEnvoyerStatutsFacturesAchatResultat, AeFMarquerStatutsFacturesAchatResultat, AeFSelectionStatutsAEnvoyerFacturesAchatParametres, EFactTraitementService} from '../../../../../../val-ammon-services/val-services-efact-lib/src/public-api';
import {ValApiResultat} from '../../../../../../val-ammon-services/val-services-communs-lib/src/public-api';

//Titre du traitement — repris dans la boîte de choix et dans la boîte de restitution
const c_Titre = "Statuts à envoyer";

//Traitement des statuts de cycle de vie à envoyer à la plateforme agréée (SU #119400)
//  Deux actions, qui sont les deux temps des jobs du même nom, restreints à la sélection :
//    - « Calculer » lit l’avancement des factures dossier du back-office et pose le statut à envoyer
//      (Prise en charge, Approuvée, En litige, Suspendue, Payée). Rien n’est transmis.
//    - « Envoyer » transmet à la plateforme agréée les statuts en attente. Irréversible côté fournisseur.
//  Aucune action n’est présélectionnée : l’envoi ne doit pas s’obtenir par une simple validation.
@Component (
{
  selector: 'val-statuts-a-envoyer-factures-achat',
  imports:
  [
    KENDO_DROPDOWNS,
    BoiteDialogueGeneriqueComponent,
    BoiteDialogueMessagesComponent,
    SimplePanelComponent,
    ControleConteneurComponent
  ],
  templateUrl: './statuts-a-envoyer-factures-achat.component.html',
  styleUrl: './statuts-a-envoyer-factures-achat.component.scss'
})
export class StatutsAEnvoyerFacturesAchatComponent
{
  //Création
  //  Paramètres
  //    eFactTraitementService : Service e-Facture
  constructor (private eFactTraitementService: EFactTraitementService)
  {
    this.titre = c_Titre;
    this.tailleLabels = 90;
    this.nombreFacturesSelectionnees = 0;
    this.actions =
    [
      {libelle: "Calculer les statuts à envoyer", envoyer: false},
      {libelle: "Envoyer les statuts à la plateforme agréée", envoyer: true}
    ];
    this.actionSelectionnee = null;
  }

  //Boîte de dialogue de choix de l’action
  @ViewChild ("boiteDialogue")
  public boiteDialogue!: BoiteDialogueGeneriqueComponent<void>;

  //Boîte de dialogue de restitution
  @ViewChild ("boiteDialogueMessages")
  public boiteDialogueMessages!: BoiteDialogueMessagesComponent;

  //Titre du traitement
  public titre: string;

  //Taille des labels
  public tailleLabels: number;

  //Nombre de factures de la sélection en cours
  public nombreFacturesSelectionnees: number;

  //Actions proposées
  public actions: DonneesActionStatutsAEnvoyer[];

  //Action sélectionnée
  public actionSelectionnee: DonneesActionStatutsAEnvoyer | null;

  //Exécute le traitement
  //  Paramètres
  //    identifiantsFactures : Identifiants des factures sélectionnées dans la grille
  //  Retourne
  //    Vrai si au moins une facture a changé — la grille doit alors être rafraîchie
  public async Executer (identifiantsFactures: string[]) : Promise<boolean>
  {
    let action: DonneesActionStatutsAEnvoyer | null;

    if (identifiantsFactures.length === 0)
      return false;

    action = await this.ChoisirAction (identifiantsFactures.length);

    if (action === null)
      return false;

    return action.envoyer ? this.Envoyer (identifiantsFactures) : this.Calculer (identifiantsFactures);
  }

  //Ouvre la boîte de choix de l’action
  //  Paramètres
  //    nombreFactures : Nombre de factures de la sélection
  //  Retourne
  //    Action retenue, nulle si l’utilisateur a annulé
  private async ChoisirAction (nombreFactures: number) : Promise<DonneesActionStatutsAEnvoyer | null>
  {
    this.actionSelectionnee = null;
    this.nombreFacturesSelectionnees = nombreFactures;

    await this.boiteDialogue.Ouvrir ();

    return this.actionSelectionnee;
  }

  //Calcule les statuts à envoyer de la sélection
  //  Paramètres
  //    identifiantsFactures : Identifiants des factures sélectionnées
  //  Retourne
  //    Vrai si au moins une facture a été marquée
  private async Calculer (identifiantsFactures: string[]) : Promise<boolean>
  {
    let valApiResultat: ValApiResultat<AeFMarquerStatutsFacturesAchatResultat>;
    let resultat: AeFMarquerStatutsFacturesAchatResultat;
    let messages: string[];

    try
    {
      valApiResultat = await this.eFactTraitementService.MarquerStatutsFacturesAchat (this.CreerParametres (identifiantsFactures));

      if (valApiResultat.status !== "success")
        return this.RestituerEchec (valApiResultat.message);

      resultat = valApiResultat.resultat ?? new AeFMarquerStatutsFacturesAchatResultat ();

      messages = [`${resultat.nombreFacturesMarquees} facture(s) marquée(s) d’un statut à envoyer, sur ${resultat.nombreFacturesExaminees} examinée(s).`];

      for (const codeStatut of Object.keys (resultat.nombreFacturesMarqueesParStatut))
        messages.push (`• ${codeStatut} : ${resultat.nombreFacturesMarqueesParStatut[codeStatut]} facture(s)`);

      //Une facture bloquée sur un motif non typé ne produit aucun statut : le dire, sans quoi l’utilisateur
      //attendrait un litige ou une suspension qui ne partira jamais.
      if (resultat.nombreFacturesBloqueesMotifNonType > 0)
        messages.push (`${resultat.nombreFacturesBloqueesMotifNonType} facture(s) laissée(s) en l’état : bloquée(s) sur un motif sans type de blocage (Litige / Suspension) paramétré.`);

      await this.Restituer ("information.png", messages);

      return resultat.nombreFacturesMarquees > 0;
    }
    catch (erreur)
    {
      return this.RestituerErreurTechnique (erreur);
    }
  }

  //Envoie les statuts à envoyer de la sélection
  //  Paramètres
  //    identifiantsFactures : Identifiants des factures sélectionnées
  //  Retourne
  //    Vrai si au moins un statut a été soumis ou effacé
  private async Envoyer (identifiantsFactures: string[]) : Promise<boolean>
  {
    let valApiResultat: ValApiResultat<AeFEnvoyerStatutsFacturesAchatResultat>;
    let resultat: AeFEnvoyerStatutsFacturesAchatResultat;
    let messages: string[];

    try
    {
      valApiResultat = await this.eFactTraitementService.EnvoyerStatutsFacturesAchat (this.CreerParametres (identifiantsFactures));

      resultat = valApiResultat.resultat ?? new AeFEnvoyerStatutsFacturesAchatResultat ();

      messages = [`${resultat.nombreFacturesSoumises} statut(s) soumis à la plateforme agréée, ${resultat.nombreFacturesAcquittees} transmission(s) tracée(s).`];

      //Une transmission non tracée laisse la facture dans « Statuts à envoyer » : elle repartira.
      if (resultat.nombreFacturesSoumises > resultat.nombreFacturesAcquittees)
        messages.push (`${resultat.nombreFacturesSoumises - resultat.nombreFacturesAcquittees} facture(s) restée(s) dans « Statuts à envoyer » : la transmission n’a pas été confirmée.`);

      //Un statut calculé sur un statut courant qui a changé depuis n’est pas transmis : il ferait reculer la facture.
      if (resultat.nombreFacturesEcartees > 0)
        messages.push (`${resultat.nombreFacturesEcartees} statut(s) en attente effacé(s) sans être transmis : le statut de la facture a changé depuis le calcul.`);

      if (valApiResultat.status !== "success" && valApiResultat.message && valApiResultat.message.length > 0)
        messages.push (valApiResultat.message);

      await this.Restituer (valApiResultat.status === "success" ? "information.png" : "exclamation.png", messages);

      return resultat.nombreFacturesSoumises > 0 || resultat.nombreFacturesEcartees > 0;
    }
    catch (erreur)
    {
      return this.RestituerErreurTechnique (erreur);
    }
  }

  //Crée les paramètres de la sélection
  //  Paramètres
  //    identifiantsFactures : Identifiants des factures sélectionnées
  //  Retourne
  //    Paramètres de l’appel
  private CreerParametres (identifiantsFactures: string[]) : AeFSelectionStatutsAEnvoyerFacturesAchatParametres
  {
    let parametres: AeFSelectionStatutsAEnvoyerFacturesAchatParametres;

    parametres = new AeFSelectionStatutsAEnvoyerFacturesAchatParametres ();
    parametres.identifiantsFactures = identifiantsFactures;

    return parametres;
  }

  //Restitue un échec fonctionnel
  //  Paramètres
  //    message : Message rendu par l’API
  //  Retourne
  //    Faux : rien n’a changé
  private async RestituerEchec (message: string | null | undefined) : Promise<boolean>
  {
    await this.Restituer ("exclamation.png", [message && message.length > 0 ? message : `Le traitement « ${c_Titre} » a échoué.`]);

    return false;
  }

  //Restitue une erreur technique
  //  Paramètres
  //    erreur : Erreur interceptée
  //  Retourne
  //    Faux : rien n’a changé
  private async RestituerErreurTechnique (erreur: unknown) : Promise<boolean>
  {
    let messages: string[];

    messages = [`Une erreur technique est survenue lors du traitement « ${c_Titre} ».`];

    if (erreur != null && typeof (erreur as {message?: unknown}).message === "string")
      messages.push ((erreur as {message: string}).message);

    await this.Restituer ("exclamation.png", messages);

    return false;
  }

  //Ouvre la boîte de restitution
  //  Paramètres
  //    icone    : Icone de la boîte
  //    messages : Messages à afficher
  private async Restituer (icone: string, messages: string[]) : Promise<void>
  {
    this.boiteDialogueMessages.titre = c_Titre;
    this.boiteDialogueMessages.icone = icone;
    this.boiteDialogueMessages.messages = messages;

    await this.boiteDialogueMessages.Ouvrir ();
  }

  //Valide le choix de l’action
  public Valider () : void
  {
    this.boiteDialogue.fonctionRetour ();
    this.boiteDialogue.Fermer ();
  }

  //Annule le choix de l’action
  public Annuler () : void
  {
    this.actionSelectionnee = null;

    this.boiteDialogue.fonctionRetour ();
    this.boiteDialogue.Fermer ();
  }
}

//Représente une action proposée sur les statuts à envoyer
export class DonneesActionStatutsAEnvoyer
{
  //Libellé présenté dans la liste
  public libelle!: string;

  //Vrai pour l’envoi à la plateforme agréée, faux pour le calcul
  public envoyer!: boolean;
}
